<template>
  <div style="width: 100%;" :style="filtersVisible ? 'display: flex;' : 'display: none;'">
    <el-col>
      <el-row type="flex" justify="left" align="middle" class="v-padded-s" id="filter-container" :gutter="40">
        <el-col v-for="(f, i) in filtersWithProps" :key="i" :span="`${f.type === 'checkboxFilter' ? '4' : filterSize}`" :style="`padding: ${f.stylePadding || '0 24px'};`">
          <template>
            <slot v-if="f.defaultSelection" :name="`${f.type}${i}`" :properties="f.props"></slot>
            <slot v-else :name="`${f.type}${i}`" :properties="f.props"></slot>
          </template>
        </el-col>
        <el-col v-if="!autoApply" :span="4">
          <el-button @click="applyFilters" :disabled="applyDisabled">
            Find <b-spinner small v-if="filtering"></b-spinner>
          </el-button>
        </el-col>
      </el-row>
    </el-col>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'

  export default {
    props: {
      filters: Array,
      localSearch: String,
      filtersVisible: Boolean,
      filterSize: Number,
      searchAction: String,
      clearAction: String,
      clearMetadataAction: String,
      transactionType: String,
      autoApply: { 'default': false, 'type': Boolean },
      searchForDeps: { 'default': true, 'type': Boolean },
      applyDisabled: { 'default': false, 'type': Boolean },
      lineFieldSearch: null,
      defaultParams: {}
    },
    data () {
      return {
        keyword: '',
        select: {},
        autocomplete: {},
        checked: {},
        filtering: false,
        filteringError: ''
      }
    },
    methods: {
      /*
      * Applies whatever filters have been set
      */
      async applyFilters () {
        this.setKeywordFilter()
        this.setCheckboxFilter()
        this.setSelectFilter()
        this.setAutocompleteFilter()
        const { localSearch, searchAction, clearAction, clearMetadataAction, searchForDeps, lineFieldSearch } = this
        this.filtering = true
        this.filteringError = ''
        const opts = { searchAction, clearAction, clearMetadataAction, searchForDeps }
        if (lineFieldSearch && lineFieldSearch.field && lineFieldSearch.transactionType) {
          opts.lineFieldSearch = lineFieldSearch
        }
        this.$emit('applied')
        if (localSearch) {
          this.$emit('localSearch')
        } else {
          try {
            await this.$store.dispatch('applyFilters', opts)
          } catch (e) {
            this.filtering = false
            this.filteringError = e
            console.log(e)
          } finally {
            this.filtering = false
            this.$emit('doneFiltering')
          }
        }
      },
      /**
       * Resets a filter type to its original state
       * @param filter  The filter type to reset
       */
      setOriginalFilterState (filter) {
        if (filter === 'select' || filter === 'autocomplete') {
          return Object.assign({})
        }
        return ''
      },
      /**
       * Emits a 'filterCleared' event when a filter is cleared. This only applies to keyword and autocomplete filters.
       * @param filtersToClear   The type of filter to clear
       */
      clearFilter (filtersToClear) {
        filtersToClear.forEach(filterName => {
          // Reset the local filter state
          this[filterName] = this.setOriginalFilterState(filterName)
          // Remove the filter from Vuex state if applicable
          this.$store.dispatch('unsetFilter', filterName)
          // Emit an event to notify that the filter has been cleared
          this.$emit('filterCleared', filterName)
        })

        // Ensure currentFilters are in sync with the Vuex store
        this.currentFilters = { ...this.$store.state.filters.filters }
      },
      /**
       * Syncs the keyword value from a keyword filter child component to this.keyword. This is because the keyword
       * filter's value cannot be set directly from the keywordFilter component on input change.
       * @param keyword   The keyword filter criteria
       */
      syncKeyword (keyword) {
        if (this.keyword !== keyword) {
          this.$emit('filterChanged', { filterName: 'keyword', selectedVal: keyword })
        }
        this.keyword = keyword || ''
      },
      syncCheckbox (filterName, selectedVal) {
        if (this.checked[filterName] !== selectedVal) {
          this.$emit('filterChanged', { filterName, selectedVal })
        }
        this.checked[filterName] = selectedVal || ''
      },
      /**
       * Syncs the value from a select filter child component to this.keyword. This is because the keyword
       * filter's value cannot be set directly from the selectFilter component on input change.
       * @param filterName    The name of the parameter by which to filter
       * @param selectedVal   The keyword filter criteria
       */
      syncSelectValues (filterName, selectedVal) {
        if (this.select[filterName] !== selectedVal) {
          this.$emit('filterChanged', { filterName, selectedVal })
        }
        this.select[filterName] = selectedVal || ''
      },
      /**
       * Syncs the value from an autocomplete filter child component to this.keyword. This is because the keyword
       * filter's value cannot be set directly from the autocompleteFilter component on input change.
       * @param filterName    The name of the parameter by which to filter
       * @param selection     The keyword filter criteria
       */
      syncAutocompleteValues (filterName, selection) {
        if (this.autocomplete[filterName] !== selection) {
          this.$emit('filterChanged', { filterName, selectedVal: selection })
        }
        this.autocomplete[filterName] = selection || ''
      },
      /**
       * Explicitly sets or unsets the keyword filter value when called
       */
      setKeywordFilter () {
        if (this.keyword && this.keyword.length) {
          this.$store.dispatch('setFilter', { search: this.keyword, ...this.defaultParams })
          this.$store.dispatch(`set${this.transactionType}FilterInfo`, {keyword: this.keyword, ...this.defaultParams})
        } else {
          this.$store.dispatch('unsetFilter', 'search')
          this.$store.dispatch(`set${this.transactionType}FilterInfo`, {keyword: '', ...this.defaultParams})
        }
      },
      setCheckboxFilter () {
        for (let filterName in this.checked) {
          if (!this.checked.hasOwnProperty(filterName)) continue
          let selection = this.checked[filterName]
          if (selection) {
            let params = {...this.defaultParams}
            params[filterName] = selection
            this.$store.dispatch('setFilter', params)
            this.$store.dispatch(`set${this.transactionType}FilterInfo`, { [`${filterName}`]: selection })
          } else {
            this.$store.dispatch('unsetFilter', filterName)
            this.$store.dispatch(`set${this.transactionType}FilterInfo`, { [`${filterName}`]: '' })
          }
        }
      },
      /**
       * Explicitly sets or unsets the select filter value when called
       */
      setSelectFilter () {
        for (let filterName in this.select) {
          if (!this.select.hasOwnProperty(filterName)) continue
          let val = this.select[filterName]
          let payload = {...this.defaultParams}
          if (val && val.length) {
            if (Array.isArray(val)) {
              payload[`${filterName}__in`] = val
              this.$store.dispatch(`set${this.transactionType}FilterInfo`, { [`${filterName}__in`]: val })
            } else {
              payload[filterName] = val
              this.$store.dispatch(`set${this.transactionType}FilterInfo`, { [filterName]: val })
            }
            this.$store.dispatch('setFilter', payload)
          } else {
            if (Array.isArray(val)) {
              this.$store.dispatch('unsetFilter', `${filterName}__in`)
              this.$store.dispatch(`set${this.transactionType}FilterInfo`, { [`${filterName}__in`]: [] })
            } else {
              this.$store.dispatch('unsetFilter', filterName)
              this.$store.dispatch(`set${this.transactionType}FilterInfo`, { [filterName]: [] })
            }
          }
        }
      },
      /**
       * Explicitly sets or unsets an autocomplete filter value when called
       */
      setAutocompleteFilter () {
        for (let filterName in this.autocomplete) {
          if (!this.autocomplete.hasOwnProperty(filterName)) continue
          let selection = this.autocomplete[filterName]
          if (selection) {
            let params = {...this.defaultParams}
            params[filterName] = selection
            this.$store.dispatch('setFilter', params)
            this.$store.dispatch(`set${this.transactionType}FilterInfo`, { [`${filterName}`]: selection })
          } else {
            this.$store.dispatch('unsetFilter', filterName)
            this.$store.dispatch(`set${this.transactionType}FilterInfo`, { [`${filterName}`]: '' })
          }
        }
      }
    },
    watch: {
      getFilters () {
        if (!Object.keys(this.getFilters).length) {   // Filter is cleared
          this.autocomplete = this.setOriginalFilterState('autocomplete')
          this.select = this.setOriginalFilterState('select')
          this.keyword = this.setOriginalFilterState()
        }
        if (this.autoApply) {
          this.searchTimeout = setTimeout(() => this.applyFilters(), 500)
        }
      }
    },
    computed: {
      ...mapGetters({
        getFilters: 'getFilters'
      }),
      /**
       * Adds the various method to the props that are passed to dynamically generated filter components (in slots)
       * so that they can be called from within individual filter components. This is useful when binding the enter
       * key to a keyword filter component's input field. Also, functions need to be passed as props. Why? Because since
       * each filter is a dynamically generated component passed into a slot (see template above), its parent scope is
       * *not* this (Filters.vue), but rather the component in which it is rendered. This means that you cannot define
       * these functions in the filters themselves and simply use @/v-on in this template to catch the return value.
       * @return {array}
       */
      filtersWithProps () {
        return this.filters.map(v => {
          switch (v.type) {
            case 'keywordFilter':
              v.props.sync = this.syncKeyword
              break
            case 'checkboxFilter':
              v.props.sync = this.syncCheckbox
              break
            case 'autocompleteFilter':
              v.props.sync = this.syncAutocompleteValues
              break
            case 'selectFilter':
              v.props.sync = this.syncSelectValues
          }
          v.props.apply = this.applyFilters
          v.props.clear = this.clearFilter
          return v
        })
      }
    }
  }
</script>

<style scoped>
  .v-padded-s {
    padding: 12px 0;
  }
</style>


// WEBPACK FOOTER //
// src/components/RetainedFilters.vue